import React, { useEffect, useState } from 'react';

import { CheckRounded, CloseRounded, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';

import { IIPENSessionData } from '../../../types/IPENSessionData';
import { IIPENNotaryNewRegistrationObj } from '../../../types/Notary';
import { ISigner } from '../../../types/Parties';
import { IContactUpdate, IUpdateSigner } from '../../../types/UpdateSigner';
import Alert from '../../components/Alert';
import AddCoSettlementAgent from '../../components/Dialogs/AddCoSettlmentAgent';
import AssignOrChangeNotaryForOrder from '../../components/Dialogs/AssignOrChangeNotaryForOrder';
import BasicConfirm from '../../components/Dialogs/BasicConfirm';
import IPENSessionModal from '../../components/Dialogs/IPENSessionModal';
import LoanCloser from '../../components/Dialogs/LoanCloser';
import OrderDetailsPopover from '../../components/Dialogs/OrderDetailsPopover';
import ProductModal from '../../components/Dialogs/ProductModal';
import RouterPrompt from '../../components/Dialogs/RouterPrompt';
import { SignerUpdateModal } from '../../components/Dialogs/SignerUpdate';
import ViewHistory from '../../components/Dialogs/ViewHistory';
import EnqueueSnackbarAction from '../../components/EnqueueSnackbarAction/EnqueueSnackbarAction';
import OptionalActions from '../../components/OptionalActions';
import OrderDetailsAccordion from '../../components/OrderDetailsAccordion/OrderDetailsAccordion';
import OrderDetailsHeader from '../../components/OrderDetailsHeader';
import SolexIPENActions from '../../components/SolexIpenActions/SolexIPENActions';
import Loading from '../../components/loading';
import { OrderStatus } from '../../constants/OrderStatus';
import { black } from '../../constants/colors';
import { docTagProduct } from '../../constants/docEditorWalkThru';
import { ipenSessionWalkThru } from '../../constants/ipenSessionWalkThru';
import { OrderAction } from '../../constants/orderActions';
import {
  SignerType,
  isBorrower,
  isNPE,
  isSignerOfType,
} from '../../constants/solexSignerTypes';
import useExtractTagDocs from '../../hooks/useExtractTagDocs';
import useOrderActions from '../../hooks/useOrderActions';
import useOrderReloader from '../../hooks/useOrderReloader';
import useOrderStates from '../../hooks/useOrderStates';
import {
  confirmNotaryRequestAsync,
  getNotaryByEmailAsync,
  resendRegistrationEmailAsync,
  searchNotariesAsync,
  submitRegistrationEmailsAsync,
} from '../../redux-store/ipenNotary/ipenNotarySlice';
import {
  setIsActiveSession,
  setIsSessionComplete,
} from '../../redux-store/ipenSession/ipenSessionSlice';
import {
  closeSnackbar,
  enqueueSnackbar,
} from '../../redux-store/notifications/notificationsSlice';
import {
  changeNotaryAsync,
  fetchSettlementAgentAsync,
  setAlert,
  updateSignerInformationAsync,
} from '../../redux-store/orders/ordersSlice';
import { IOrderStatus, IPopoverContent } from '../../type';
import { solexErrorFormatter, updatedSearchParams } from '../../utils/helpers';
import { getIPENSessionData } from '../../utils/ipenSessionData';

export default function IPENCXLandingPage() {
  // Custom Hooks
  const {
    params,
    orderDetailsRef,
    documentRef,
    scrollToOrder,
    scrollToDocument,
    form,
    input,
    order,
    navigate,
    dispatch,
    orders,
    isFetching,
    isFetchingText,
    alertUser,
    ipenSession,
    account,
    ipenNotary,
    showAddCoSABtn,
    isCurrentUserEscrowOnOrder,
    isCurrentUserEscrowOfficerOnOrder,
    existingSettlementAgentsOnTheOrder,
    isThirdPartyOrder,
    havePendingNotary,
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    isOrderReviewedByNotary,
    isDocumentReviewedByNotary,
    isIdentifyWitnessPartyByNotary,
    notaryAssigned,
    isCurrentUserNotaryOnOrder,
    closingDate,
    preClosingActive,
    onClosingActive,
    postClosingActive,
    notarySigned,
    orderWitnesses,
    orderContainsWitnessSigners,
    orderContainsNPE,
    signerEvents,
    orderStatus,
    currentUserName,
    historiesToShow,
  } = useOrderStates();

  useOrderReloader(order);
  const { docsTagged, docsEdited, tagDocsLink } = useExtractTagDocs(
    order,
    currentUserName
  );
  const {
    getToken,
    goResetLogin,
    goUnlockSession,
    handleAddCosettlementAgents,
    handleUpdateOrderActions,
    handleCancelConfirmActions,
  } = useOrderActions(order, currentUserName);
  const initialPopoverContent: IPopoverContent = {
    title: '',
    firstBtnText: 'I HAVE REVIEWED',
    secondBtnText: '',
  };

  // Local States
  const [openAddCoSa, setOpenAddCoSa] = useState<boolean>(false);
  const [openWitnessInfoModal, setOpenWitnessInfoModal] =
    useState<boolean>(false);
  const [notaryPanel, setNotaryPanel] = useState<boolean>(false);
  const [partyPanel, setPartyPanel] = useState<boolean>(false);
  const [settlementPanel, setSettlementPanel] = useState<boolean>(false);
  const [orderDetailsPanel, setOrderDetailsPanel] = useState<boolean>(false);
  const [downloadPanel, setDownloadPanel] = useState<boolean>(false);
  const [popoverContent, setPopoverContent] = useState<IPopoverContent>(
    initialPopoverContent
  );
  const [openPopover, setOpenPopover] = useState<any>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openReport, setOpenReport] = useState<boolean>(false);
  const [arrowRef, setArrowRef] = useState<any>(null);
  const [orderZIndex, setOrderZIndex] = useState<number>(0);
  const [documentZIndex, setDocumentZIndex] = useState<number>(0);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [openSettlementHistory, setOpenSettlementHistory] =
    useState<boolean>(false);
  const [orderProgressStatus, setOrderProgressStatus] = useState<IOrderStatus>({
    name: 'IN PROGRESS',
    color: 'warning',
  });
  const [uploadSelectError, setUploadSelectError] = useState<string | null>(
    null
  );
  const [openTagDocsModal, setOpenTagDocsModal] = useState<boolean>(false);
  const [saToken, setSaToken] = useState<string>('');

  // IPEN Session Local States
  const [openAssignNotary, setOpenAssignNotary] = useState<boolean>(false);
  const [openIPENSessionModal, setOpenIPENSessionModal] =
    useState<boolean>(false);
  const [ipenSessionData, setIpenSessionData] =
    useState<IIPENSessionData | null>(null);
  const [notaryInfoConfirmed, setNotaryInfoConfirmed] =
    useState<boolean>(false);
  const [isFetchingNotary, setIsFetchingNotary] = useState<boolean>(false);
  const [showIPENOverview, setShowIPENOverview] = useState<boolean>(false);
  const [showCancelConfirmation, setShowCancelConfirmation] =
    useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const [sessionLaunching, setSessionLaunching] = useState<boolean>(false);

  // useEffects
  useEffect(() => {
    if (
      isCurrentUserEscrowOnOrder &&
      orderStatus === OrderStatus.OpenAllSigned &&
      notaryAssigned
    ) {
      setOrderProgressStatus({
        name: 'COMPLETE',
        color: 'success',
      });
    } else if (
      isCurrentUserNotaryOnOrder &&
      orderStatus === OrderStatus.OpenAllSigned
    ) {
      setOrderProgressStatus({
        name: 'COMPLETE',
        color: 'success',
      });
    }
  }, [
    orderStatus,
    notaryAssigned,
    isCurrentUserEscrowOnOrder,
    isCurrentUserNotaryOnOrder,
  ]);

  useEffect(() => {
    const orderStatus = order?.Status;
    if (ipenSession) {
      const sessionData: IIPENSessionData | null = getIPENSessionData(
        order,
        ipenSession,
        account
      );
      if (sessionData) {
        setIpenSessionData(sessionData);
      } else {
        setIpenSessionData(null);
      }
    }
    if (orderStatus === OrderStatus.OpenAllSigned) {
      dispatch(setIsActiveSession(false));
      dispatch(setIsSessionComplete(true));
    } else {
      dispatch(setIsSessionComplete(false));
    }
  }, [order]);

  useEffect(() => {
    if (openAssignNotary) {
      // Don't do an initial search when notary exists as the dropdown component will get notaries based off the existing notary name
      if (!order?.OrderDetails?.IPEN?.Notary?.FullName) {
        const threeDaysAfterSigningDate: Date = new Date(
          order?.OrderDetails?.SigningDate
        );
        const daysToAdd = threeDaysAfterSigningDate.getDate() + 3;
        threeDaysAfterSigningDate.setDate(daysToAdd);
        const searchParams = updatedSearchParams(
          15,
          'Active,Pending',
          threeDaysAfterSigningDate,
          true
        );
        dispatch(
          searchNotariesAsync({ parameters: searchParams, loadMore: false })
        );
      }
      sessionStorage.removeItem('previousQuery');
      setNotaryPanel(true);
    }
  }, [openAssignNotary, openAddCoSa]);

  useEffect(() => {
    if (isCurrentUserEscrowOnOrder) {
      setSettlementPanel(true);
    } else if (isCurrentUserNotaryOnOrder) {
      setSettlementPanel(false);
      setNotaryPanel(true);
      setPartyPanel(true);
    }
  }, [isCurrentUserEscrowOnOrder, isCurrentUserNotaryOnOrder]);

  useEffect(() => {
    const partyLink = ipenSessionData?.tagDocsLink;
    const fetchSAAccessToken = async () => {
      if (partyLink) {
        getToken(partyLink).then((response: any) => {
          if (response && response.payload.length > 0) {
            setSaToken(response.payload);
          }
        });
      }
    };
    if (!ipenNotary.isFetching && partyLink && saToken.length === 0) {
      fetchSAAccessToken();
    }
  }, [ipenSessionData]);

  const handleWitnessInfoUpdate = (values: ISigner[]) => {
    const valuesToSubmit: IContactUpdate[] = [];
    values.map((signer: ISigner) => {
      valuesToSubmit.push({
        PartyId: signer.SolexSigner?.SolexId?.toString() || '',
        Name: {
          FirstName: signer.FirstName || '',
          LastName: signer.LastName || '',
          MiddleName: signer.MiddleName || '',
          Suffix: signer.Suffix || '',
        },
      });
    });
    const data: IUpdateSigner = {
      ContactUpdates: valuesToSubmit,
      SessionId: order.SolexSessionId,
    };
    dispatch(
      updateSignerInformationAsync({
        OrderId: order.Id,
        SignerInformation: data,
      })
    ).then(() => {
      handleUpdateOrderActions(
        OrderAction.OnClosing_Notary_IdentifyWitnessInformation,
        true
      );
    });
    setOpenWitnessInfoModal(false);
  };

  const onSaActionsClick = (item: number) => {
    switch (item) {
      case 0: {
        setAnchorEl(orderDetailsRef.current);
        scrollToOrder();
        setOrderDetailsPanel(true);
        setPartyPanel(true);
        setSettlementPanel(false);
        setDownloadPanel(false);
        if (!isOrderReviewedBySA) {
          setOpenPopover(true);
          setPopoverContent({
            title:
              'Please review the Order Details and Party Information for accuracy. When complete, please click I HAVE REVIEWED.',
            firstBtnText: 'I HAVE REVIEWED',
            secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
              ? 'INACCURATE INFORMATION'
              : '',
            action: goSAReviewOrder,
          });
          setShowOverlay(true);
          setOrderZIndex(97);
          setDocumentZIndex(0);
        }
        break;
      }
      case 1: {
        setAnchorEl(documentRef.current);
        scrollToDocument();
        setDownloadPanel(true);
        setOrderDetailsPanel(false);
        setPartyPanel(false);
        setSettlementPanel(false);
        if (!isDocumentReviewedBySA) {
          setOpenPopover(true);
          setPopoverContent({
            title:
              'Please review the Order documents for accuracy. When complete, please click I HAVE REVIEWED.',
            firstBtnText: 'I HAVE REVIEWED',
            secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
              ? 'INACCURATE INFORMATION'
              : '',
            action: goSAReviewDocuments,
          });
          setShowOverlay(true);
          setDocumentZIndex(97);
          setOrderZIndex(0);
        }
        break;
      }
      case 2: {
        if (!docsTagged) {
          setOpenTagDocsModal(true);
          setDownloadPanel(true);
        }
        break;
      }
      case 3: {
        if (!notaryAssigned) {
          setOpenAssignNotary(true);
          setSettlementPanel(false);
          setDownloadPanel(false);
          setNotaryPanel(true);
          setPartyPanel(false);
          setOrderDetailsPanel(false);
        }
        break;
      }
    }
  };

  const onNotaryActionsClick = (item: number) => {
    switch (item) {
      case 0: {
        setAnchorEl(orderDetailsRef.current);
        scrollToOrder();
        setOrderDetailsPanel(true);
        setPartyPanel(true);
        setNotaryPanel(false);
        if (!isOrderReviewedByNotary) {
          setOpenPopover(true);
          setPopoverContent({
            title:
              'Please review the Order Details and Party Information for accuracy. When complete, please click I HAVE REVIEWED.',
            firstBtnText: 'I HAVE REVIEWED',
            secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
              ? 'INACCURATE INFORMATION'
              : '',
            action: goNotaryReviewOrder,
          });
          setShowOverlay(true);
          setOrderZIndex(97);
          setDocumentZIndex(0);
        }

        break;
      }
      case 1: {
        setAnchorEl(documentRef.current);
        scrollToDocument();
        setDownloadPanel(true);
        setOrderDetailsPanel(false);
        setPartyPanel(false);
        setNotaryPanel(false);
        if (!isDocumentReviewedByNotary) {
          setOpenPopover(true);
          setPopoverContent({
            title:
              'Please review the Order documents for accuracy. When complete, please click I HAVE REVIEWED.',
            firstBtnText: 'I HAVE REVIEWED',
            secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
              ? 'INACCURATE INFORMATION'
              : '',
            action: goNotaryReviewDocument,
          });
          setShowOverlay(true);
          setDocumentZIndex(97);
          setOrderZIndex(0);
        }
        break;
      }
    }
  };

  const reportInaccurateInformation = () => {
    setShowOverlay(false);
    setOpenReport(true);
  };

  const goSAReviewOrder = () => {
    handleUpdateOrderActions(
      OrderAction.PreClosing_SettlementAgent_ReviewOrderDetailsAndPartyInformation,
      true
    );
    setOpenPopover(false);
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setSettlementPanel(false);
    setPartyPanel(false);
  };

  const goSAReviewDocuments = () => {
    handleUpdateOrderActions(
      OrderAction.PreClosing_SettlementAgent_ReviewDocumentPackage,
      true
    );
    setOpenPopover(false);
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
    setDownloadPanel(false);
    setSettlementPanel(false);
  };

  const goNotaryReviewOrder = () => {
    handleUpdateOrderActions(
      OrderAction.PreClosing_Notary_ReviewOrderDetailsAndPartyInformation,
      true
    );
    setOpenPopover(false);
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
  };

  const goNotaryReviewDocument = () => {
    handleUpdateOrderActions(
      OrderAction.PreClosing_Notary_ReviewDocumentPackage,
      true
    );
    setOpenPopover(false);
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
    setDownloadPanel(false);
  };

  const triggerSnackbar = (
    message: string,
    variant: string,
    persist: boolean
  ) => {
    dispatch(
      enqueueSnackbar({
        message,
        key: new Date().getTime() + Math.random(),
        options: {
          variant: variant,
          persist: persist,
          action: (key: number) => <EnqueueSnackbarAction snackbarId={key} />,
        },
      })
    );
  };

  const handleClickResendLink = (notaryEmail: string) => {
    if (notaryEmail) {
      dispatch(resendRegistrationEmailAsync([notaryEmail]))
        .then((response: any) => {
          const payload = response.payload;
          // something went wrong?
          if (payload.message == null) {
            triggerSnackbar(
              'Something went wrong. Please try again later.',
              'error',
              true
            );
          }
        })
        .catch((err: any) => {
          console.error(err);
        });
    } else {
      triggerSnackbar(
        'There was an issue executing this request',
        'error',
        true
      );
    }
  };

  const openTagDocsLink = () => {
    setOpenTagDocsModal(false);
    if (tagDocsLink) {
      window.open('', 'thisWindow');
      form.current?.submit();
    }
  };

  const handleChangeNotary = (data: {
    OrderId?: string;
    NotaryEmail?: string;
  }) => {
    dispatch(changeNotaryAsync(data))
      .then(() => {
        handleUpdateOrderActions(
          OrderAction.PreClosing_SettlementAgent_ChangedNotary,
          true
        );
        triggerSnackbar(
          'Notary has been changed successfully.',
          'success',
          true
        );
      })
      .catch(() => {
        triggerSnackbar(
          'Failed to change Notary. Please try again.',
          'error',
          true
        );
      })
      .finally(() => {
        setOpenAssignNotary(false);
      });
  };

  const handleAddNotary = (data: IIPENNotaryNewRegistrationObj) => {
    dispatch(submitRegistrationEmailsAsync(data))
      .then((response: any) => {
        const payload = response.payload;
        if (payload && payload.FaultedMembers) {
          triggerSnackbar(
            `An email address you've entered is already registered or pending registration.`,
            'error',
            true
          );
        } else {
          triggerSnackbar(
            `A registration link has been sent to 1 person.`,
            'success',
            true
          );
          handleChangeNotary({
            OrderId: order.Id,
            NotaryEmail: data.Notaries[0].Email,
          });
        }
      })
      .catch(() => {
        triggerSnackbar(
          'Failed to register new notary. Please try again.',
          'error',
          true
        );
      });
  };

  const handleEnqueueSnackbar = (message: string, variant: string) => {
    dispatch(
      enqueueSnackbar({
        message: message,
        key: new Date().getTime() + Math.random(),
        options: {
          variant: variant,
          persist: true,
          action: (key: any) => (
            <IconButton
              onClick={() => {
                dispatch(closeSnackbar(key));
              }}
              color='primary'
              size='large'>
              <CloseRounded style={{ color: 'white' }} />
            </IconButton>
          ),
        },
      })
    );
  };

  const handleConfirmNotaryInfo = () => {
    setSessionLaunching(true);
    if (order?.Id && ipenNotary?.notary?.Email) {
      dispatch(confirmNotaryRequestAsync(order?.Id || '')).then(
        (response: any) => {
          if (response.error) {
            const detailedErrorMessage =
              response.payload?.response?.data?.Message ||
              'An unexpected error occurred';
            const formattedErrorMessage =
              solexErrorFormatter(detailedErrorMessage);
            handleEnqueueSnackbar(formattedErrorMessage, 'error');
            setSessionLaunching(false);
          } else {
            unlockConcurrentSignerIpenSessions().then(() => {
              setNotaryInfoConfirmed(true);
              dispatch(setIsActiveSession(true));
              setSessionLaunching(false);
            });
          }
        }
      );
    }
  };

  const unlockConcurrentSignerIpenSessions = (): Promise<void> => {
    const allConcurrentSigners = (
      ipenSessionData as IIPENSessionData
    )?.sessionSigners.filter((sessionSigner) => {
      if (isBorrower(sessionSigner.signer) || isNPE(sessionSigner.signer)) {
        return !sessionSigner.signer.SigningDelegatePartyId;
      }

      return isSignerOfType(sessionSigner.signer, SignerType.PowerOfAttorney);
    });

    if (allConcurrentSigners) {
      const concurrentSignersPartyIds = allConcurrentSigners.map(
        (sessionSigner) => sessionSigner.signer.Id
      );
      return goUnlockSession(concurrentSignersPartyIds);
    }

    return Promise.resolve();
  };

  const handleFetchNotary = () => {
    const email = account?.currentUser?.email.toLowerCase();
    if (email.toLowerCase() && !ipenSessionData?.sessionActive) {
      setIsFetchingNotary(true);
      dispatch(getNotaryByEmailAsync(email)).then(() => {
        setIsFetchingNotary(false);
      });
    }
  };

  const [existingCoSa, setExcistingCoSa] = useState<any>(null);

  const handleGetExistingSettlementAgentByEmail = (saEmail: string) => {
    dispatch(fetchSettlementAgentAsync(saEmail)).then((response: any) => {
      if (response.payload) {
        setExcistingCoSa(response.payload);
        return response.payload;
      }
    });
  };

  return (
    <>
      {orders.isLoading && (
        <Box className='full-page-loader'>
          <Typography variant='h6'>{'Loading Order Details...'}</Typography>
          <CircularProgress />
        </Box>
      )}
      {ipenNotary?.isReSending && (
        <Box className='full-page-loader'>
          <Typography variant='h6'>
            {'Resending registration email...'}
          </Typography>
          <CircularProgress />
        </Box>
      )}
      {isFetching && (
        <div className='fetching-spinner'>
          <Loading text={isFetchingText ? isFetchingText : 'Loading'} />
        </div>
      )}
      {/* This is for the Upload and Tag Title Documents section */}
      <form action={tagDocsLink} ref={form} method='POST' target='thisWindow'>
        <input ref={input} type='hidden' name='access_token' value={saToken} />
      </form>
      {/* Confirm order cancellation */}
      <BasicConfirm
        open={showCancelConfirmation}
        title='Confirm order cancelation'
        showTitle
        content='Are you sure you want to cancel this order? Canceled orders cannot be reopened.'
        primaryAction={(e, a) => {
          setShowCancelConfirmation(false);
          handleCancelConfirmActions(e, a);
        }}
        primaryActionText='Confirm Order Cancelation'
        secondaryAction={(e, a) => {
          setShowCancelConfirmation(false);
          handleCancelConfirmActions(e, a);
        }}
        secondaryActionText='Do not Cancel'
      />
      {/* Confirm navigating away from IPEN Session */}
      <RouterPrompt when={ipenSession.isActiveSession}>
        {({ isActive, onConfirm, onCancel }) => (
          <BasicConfirm
            open={isActive}
            title='IPEN Session In Progress'
            showTitle
            content='Are you sure you want to leave your active IPEN Session?'
            primaryAction={onConfirm}
            primaryActionText='Leave Session'
            secondaryAction={onCancel}
            secondaryActionText='Cancel'
          />
        )}
      </RouterPrompt>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertUser !== null}
        autoHideDuration={6000}
        onClose={() => {
          if (alertUser) dispatch(setAlert(null));
          if (uploadSelectError) setUploadSelectError(null);
        }}>
        <div>
          <Alert
            text={alertUser?.text || uploadSelectError}
            type={alertUser?.type}
            icon={
              alertUser?.type === 'error' ? <InfoOutlined /> : <CheckRounded />
            }
            elevated
            bold
          />
        </div>
      </Snackbar>
      {showOverlay && (
        <div
          style={{
            backgroundColor: black,
            opacity: 0.5,
            width: '100%',
            height: '100%',
            zIndex: 97,
            top: 0,
            left: 0,
            position: 'fixed',
          }}></div>
      )}
      <SignerUpdateModal
        isOpen={openWitnessInfoModal}
        onClose={() => setOpenWitnessInfoModal(false)}
        signersToUpdate={orderWitnesses}
        submitUpdate={handleWitnessInfoUpdate}
        isLoading={orders.isIdentifingWitness}
      />
      <AssignOrChangeNotaryForOrder
        order={order}
        open={openAssignNotary}
        title='Please select or invite a notary for this order'
        onClose={() => setOpenAssignNotary(false)}
        isLoading={ipenNotary?.isLoading}
        isSending={ipenNotary?.isSending}
        isReassigningNotary={orders.isReassigningNotary}
        changeNotary={(params) => handleChangeNotary(params)}
        addNotary={(params) => handleAddNotary(params)}
      />
      <AddCoSettlementAgent
        open={openAddCoSa}
        // We set the max capacity when we know how many we want to add to the order
        maxCapacity={100}
        existingCoSa={existingCoSa}
        orderType={order.OrderType.DocGen}
        existingSettlementAgentsOnTheOrder={existingSettlementAgentsOnTheOrder}
        isAddingCoSettlement={orders.isAddingCoSettlement}
        addCosettlementAgents={(list) =>
          handleAddCosettlementAgents(list, order?.OrderType.DocGen)
        }
        getExistingSettlementAgentByEmail={(saEmail) =>
          handleGetExistingSettlementAgentByEmail(saEmail)
        }
        onClose={() => {
          setOpenAddCoSa(false);
        }}
      />
      <ViewHistory
        onClose={() => setOpenSettlementHistory(false)}
        open={openSettlementHistory}
        data={historiesToShow}
      />
      <ViewHistory
        onClose={() => setOpenDialog(false)}
        open={openDialog}
        data={order?.Events ? order?.Events : []}
      />
      <OrderDetailsPopover
        open={openPopover}
        title={popoverContent.title}
        anchorEl={anchorEl}
        arrowRef={arrowRef}
        firstBtnText={popoverContent.firstBtnText}
        secondBtnText={popoverContent.secondBtnText}
        onClose={() => {
          setShowOverlay(false);
          setPopoverContent(initialPopoverContent);
          setOpenPopover(false);
        }}
        onInaccurateClick={() => {
          reportInaccurateInformation();
        }}
        onReviewClick={popoverContent.action}
        setArrowRef={setArrowRef}
      />
      <LoanCloser
        onClose={() => {
          setOpenReport(false);
          setOpenPopover(false);
        }}
        open={openReport}
        order={order}
      />
      {/* Document tagging walk-thru */}
      <ProductModal
        title='Solex Doc Editor'
        open={openTagDocsModal}
        content={docTagProduct}
        onClose={openTagDocsLink}
      />
      {/* IPEN Session walk-thru */}
      <ProductModal
        title='Solex IPEN Session'
        open={showIPENOverview}
        content={ipenSessionWalkThru}
        onClose={() => {
          handleFetchNotary();
          setShowIPENOverview(false);
          setOpenIPENSessionModal(true);
        }}
      />
      <IPENSessionModal
        sessionData={ipenSessionData as IIPENSessionData}
        open={openIPENSessionModal}
        close={() => setOpenIPENSessionModal(false)}
        ipenNotary={order?.OrderDetails?.IPEN?.Notary}
        sessionLaunching={sessionLaunching}
        confirmNotaryInfo={handleConfirmNotaryInfo}
        notaryInfoConfirmed={notaryInfoConfirmed}
        unlockParty={goResetLogin}
        getSsoToken={getToken}
        activateSession={goUnlockSession}
        isFetchingNotary={isFetchingNotary}
        editNotaryRegistration={() =>
          navigate('/notary/registration?editMode=true')
        }
        ipenSession={ipenSession}
      />
      {!orders.isLoading && (
        <>
          <OrderDetailsHeader
            status={order.Status}
            orderStatus={orderProgressStatus}
            isGuest={account?.isGuest}
            backClicked={() => navigate('/')}
            branding={order.OrderDetails.Branding}
          />
          <Box sx={{ boxShadow: 3, backgroundColor: 'white' }}>
            <SolexIPENActions
              orderStatus={order.Status}
              orderType={order?.OrderType}
              signerEvents={signerEvents}
              isOrderReviewedBySA={isOrderReviewedBySA}
              isDocumentReviewedBySA={isDocumentReviewedBySA}
              isOrderReviewedByNotary={isOrderReviewedByNotary}
              isDocumentReviewedByNotary={isDocumentReviewedByNotary}
              isIdentifyWitnessPartyByNotary={isIdentifyWitnessPartyByNotary}
              notarySigned={notarySigned}
              notaryAssigned={notaryAssigned}
              docsEdited={docsEdited}
              docsTagged={docsTagged}
              isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
              isCurrentUserNotaryOnOrder={isCurrentUserNotaryOnOrder}
              orderContainsWitnessSigners={orderContainsWitnessSigners}
              orderContainsNPE={orderContainsNPE}
              preClosingActive={preClosingActive}
              onClosingActive={onClosingActive}
              postClosingActive={postClosingActive}
              closingDate={closingDate}
              ipenSession={ipenSession}
              setOpenWitnessInfoModal={setOpenWitnessInfoModal}
              onSaActionsClick={onSaActionsClick}
              onNotaryActionsClick={onNotaryActionsClick}
              setOpenIPENSessionModal={setShowIPENOverview}
              ipenSessionData={ipenSessionData}
            />
            <OptionalActions
              order={order}
              isIdentifyWitnessPartyByNotary={isIdentifyWitnessPartyByNotary}
              isCurrentUserNotaryOnOrder={isCurrentUserNotaryOnOrder}
              isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
              isCurrentUserEscrowOfficerOnOrder={
                isCurrentUserEscrowOfficerOnOrder && showAddCoSABtn
              }
              havePendingNotary={havePendingNotary}
              docsTagged={docsTagged}
              isThirdPartyOrder={isThirdPartyOrder}
              docsEdited={docsEdited}
              notaryAssigned={notaryAssigned}
              CancelableOrder={order?.Cancelable}
              orderStatus={order.Status}
              ipenSession={ipenSession}
              handleClickResendLink={handleClickResendLink}
              navigateToProfile={() =>
                navigate('/notary/registration?editMode=true')
              }
              setOpenTagDocsModal={setOpenTagDocsModal}
              setOpenAssignNotary={setOpenAssignNotary}
              setOpenAddCoSa={setOpenAddCoSa}
              setShowCancelConfirmation={setShowCancelConfirmation}
              setOpenWitnessInfoModal={setOpenWitnessInfoModal}
            />
          </Box>
          <OrderDetailsAccordion
            order={order}
            orderDetailsPanel={orderDetailsPanel}
            partyPanel={partyPanel}
            settlementPanel={settlementPanel}
            notaryPanel={notaryPanel}
            downloadPanel={downloadPanel}
            orderDetailsRef={orderDetailsRef}
            documentRef={documentRef}
            params={params}
            isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
            orderZIndex={orderZIndex}
            documentZIndex={documentZIndex}
            ipenSession={ipenSession}
            ipenSessionData={ipenSessionData}
            setOrderDetailsPanel={setOrderDetailsPanel}
            setOpenSettlementHistory={setOpenSettlementHistory}
            setPartyPanel={setPartyPanel}
            setNotaryPanel={setNotaryPanel}
            setSettlementPanel={setSettlementPanel}
            setOpenDialog={setOpenDialog}
            setDownloadPanel={setDownloadPanel}
            goResetLogin={goResetLogin}
            goUnlockSession={goUnlockSession}
            getSsoToken={getToken}
          />
        </>
      )}
    </>
  );
}
